import { FC, PropsWithChildren, useMemo } from "react";

interface MarkdownComponentLinkProps extends PropsWithChildren {
  href?: string;
  hrefBaseUrl?: string;
}

/**
 * Custom link component for markdown that handles:
 * - Relative links from markdown (converts to absolute URLs based on the hrefBaseUrl prop)
 * - Absolute external links (opens in new tab)
 */
export const MarkdownComponentLink: FC<MarkdownComponentLinkProps> = ({
  href = "",
  children,
  hrefBaseUrl,
}) => {
  // Check if link is relative (not an absolute URL)
  const isRelative = useMemo(() => {
    if (!href) return false;

    try {
      new URL(href);
      return false; // If URL constructor succeeds, it's absolute
    } catch {
      // If URL constructor fails, check for protocol-relative and anchors
      return !href.startsWith("//") && !href.startsWith("#");
    }
  }, [href]);

  // Convert relative links to absolute URLs based on the hrefBaseUrl prop
  const finalHref = useMemo(() => {
    if (!isRelative || !hrefBaseUrl) {
      return href;
    }

    return new URL(href, hrefBaseUrl).toString();
  }, [href, hrefBaseUrl, isRelative]);

  // Check if external link (different domain)
  const isExternal = finalHref.startsWith("http");

  return (
    <a
      href={finalHref}
      className="ae-typography"
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      data-theme="dark"
    >
      {children}
    </a>
  );
};
